import { forwardRef, type MouseEvent } from 'react';

import { Carousel } from '@debijenkorf/carousel';
import { ProductItem, type ProductItemProps } from '@debijenkorf/product-item';

import styles from './ProductCarousel.module.scss';

export type ProductCarouselItem = Pick<
  ProductItemProps,
  | 'title'
  | 'subtitle'
  | 'description'
  | 'url'
  | 'images'
  | 'price'
  | 'tags'
  | 'hasGift'
  | 'isSponsored'
> & {
  productCode: string;
  code: string;
};

export type ProductCarouselProps = {
  items: ProductCarouselItem[];
  itemLabelledbyPrefix: string;
  onWishlistChange: (position: number) => (type: 'remove' | 'add', code?: string) => void;
  combinedProductCodes?: { productCode?: string; productVariantCode?: string }[];
  currentBreakpoint?: string;
  withInlineSpace?: boolean;
  onDeleteItem?: (code: string, length?: number) => void;
  onAnalyticsEvent?: (analyticsEvent: { position: number }) => void;
};

export const ProductCarousel = forwardRef<HTMLDivElement, ProductCarouselProps>(
  (
    {
      items,
      itemLabelledbyPrefix,
      onWishlistChange,
      combinedProductCodes = [],
      currentBreakpoint = 'xs',
      withInlineSpace,
      onDeleteItem,
      onAnalyticsEvent,
    },
    ref,
  ) => {
    const handleProductClick =
      (url: string | undefined, position: number) =>
      (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        event.preventDefault();

        onAnalyticsEvent?.({ position });

        if (url) window.location.href = url;
      };

    return (
      <div ref={ref} data-testid="product-carousel">
        <Carousel currentBreakpoint={currentBreakpoint} withInlineSpace={withInlineSpace}>
          {items.map(({ productCode, ...item }, index) => {
            const position = index + 1;

            return (
              <li key={item.code} className={styles.ProductCarouselItem}>
                <ProductItem
                  {...item}
                  isLazy
                  labelledbyPrefix={`${itemLabelledbyPrefix}_item${position}`}
                  isSelected={combinedProductCodes.some(codes => codes.productCode === productCode)}
                  onWishlistChange={onWishlistChange(position)}
                  onProductClick={handleProductClick(item.url, position)}
                  onDelete={
                    onDeleteItem ? () => onDeleteItem(productCode, items.length - 1) : undefined
                  }
                />
              </li>
            );
          })}
        </Carousel>
      </div>
    );
  },
);
